import { useMemo } from 'react';
import { AggregateLiqState } from 'src/hooks/Liquidations/useAggregateLiquidationDataForUser';
import ToggleHeaderCell from 'src/components/Tables/cells/ToggleHeaderCell';
import { createColumnHelper } from '@tanstack/react-table';
import { RelativeDateDisplay } from 'src/components/Tables/RelativeDateDisplay';
import { getDescriptionForLiqRecord } from 'src/utils/liquidation';
import IconButton from 'src/components/primitives/IconButton/IconButton';
import { Details } from '@drift-labs/icons';
import Link from 'src/components/primitives/Link/Link';
import { abbreviateAddress } from '@drift/common';
import { PublicKey } from '@drift-labs/sdk';

const columnHelper = createColumnHelper<AggregateLiqState>();

interface Params {
	/**
	 * If true, renders an extra column with liquidatee data.
	 */
	showLiquidatee?: boolean;
	toggleDateDisplayType: () => void;
	displayDatesAsTimestamp: boolean;
}

export default function useLiquidationsTableColumns({
	showLiquidatee,
	toggleDateDisplayType,
	displayDatesAsTimestamp,
}: Params) {
	return useMemo(() => {
		const columnRatio = showLiquidatee ? 4 : 3;

		return [
			columnHelper.accessor('ts', {
				id: 'time',
				header: () => (
					<ToggleHeaderCell label="Time" onClick={toggleDateDisplayType} />
				),
				cell: ({ getValue }) => (
					<RelativeDateDisplay
						date={new Date(getValue() * 1000)}
						displayAsTimestamp={displayDatesAsTimestamp}
					/>
				),
				meta: {
					width: { ratio: columnRatio, minPx: 180 },
				},
			}),
			...(showLiquidatee
				? [
						columnHelper.accessor('liquidations', {
							id: 'liquidatee',
							header: 'Liquidatee',
							cell: ({ getValue }) => {
								const liquidatee = getValue()[0]?.user;

								if (!liquidatee) {
									return <span className="text-text-secondary">-</span>;
								}

								return (
									<Link
										variant="primary"
										href={`https://app.drift.trade/?userAccount=${liquidatee.toString()}`}
										openInNewTab
										onClick={(e) => e.stopPropagation()}
									>
										{abbreviateAddress(new PublicKey(liquidatee))}
									</Link>
								);
							},
							meta: {
								width: { ratio: columnRatio, minPx: 180 },
							},
						}),
					]
				: []),
			columnHelper.accessor('notionalAmount', {
				id: 'notional-amount',
				header: 'Notional Debt Transferred',
				cell: ({ getValue }) => getValue().abs().toNotional(true),
				meta: {
					width: { ratio: columnRatio, minPx: 180 },
				},
			}),
			columnHelper.accessor('summary', {
				id: 'summary',
				header: 'Liquidation Summary',
				cell: ({ row: { original } }) => getDescriptionForLiqRecord(original),
				meta: {
					width: { ratio: columnRatio, minPx: 180 },
				},
			}),
			columnHelper.display({
				id: 'action',
				header: '',
				cell: () => (
					// Click handled by row click handler.
					<IconButton size="sm" variant="tertiary" icon={Details} />
				),
				meta: {
					width: { ratio: 1, minPx: 50 },
					bodyCellClassName: 'flex items-center justify-end justify-self-end',
				},
			}),
		];
	}, [toggleDateDisplayType, displayDatesAsTimestamp, showLiquidatee]);
}
